// 环境配置
const isDevelopment = process.env.NODE_ENV === 'development'
const isProduction = process.env.NODE_ENV === 'production'
// 基础 URL 常量定义
export const TEST_BASE_URL = 'https://test.vbidding.com'      // 测试环境
export const DEVELOPMENT_BASE_URL = 'http://localhost:9500'
export const PRODUCTION_BASE_URL = 'https://www.vbidding.com'  // 生产环境
// 基础 URL 配置
export const baseUrl = isDevelopment
  ? PRODUCTION_BASE_URL      // 开发/测试环境
  : PRODUCTION_BASE_URL   // 生产环境
export const uploadBaseUrl = isDevelopment
  ? PRODUCTION_BASE_URL      // 开发/测试环境
  : PRODUCTION_BASE_URL   // 生产环境
// MQTT WebSocket 地址配置
export const MQTT_CONFIG = {
  TEST_URL: 'wss://test-mqtt.vbidding.com/mqtt',
  PRODUCTION_URL: 'wss://mqtt.vbidding.com/mqtt'
}
// 根据当前环境获取 MQTT URL
export const mqttUrl = isDevelopment
  ? MQTT_CONFIG.TEST_URL      // 开发/测试环境使用测试地址
  : MQTT_CONFIG.PRODUCTION_URL   // 生产环境使用生产地址
// 请求配置
export const requestConfig = {
  timeout: 10060000, // 请求超时时间
  debounceConfig: {
    defaultDelay: 300, // 默认防抖延迟
    searchDelay: 500, // 搜索防抖延迟
  },
  throttleConfig: {
    defaultDelay: 1000, // 默认节流延迟
    submitDelay: 2000, // 提交节流延迟
  },
  queueConfig: {
    maxConcurrent: 10, // 最大并发请求数
  }
}
// 项目类型映射
export const projectTypeMapping = {
  '01': '企业采购--公开招标项目流程',
  '02': '企业采购--竞争性谈判项目流程',
  '03': '企业采购--竞争性磋商项目流程',
  '04': '企业采购--邀请招标项目流程',
  '05': '企业采购--询价项目流程',
  '06': '企业采购--单一来源项目流程'
}

// 获取项目类型名称
export const getProjectTypeName = (projectType) => {
  return projectTypeMapping[projectType] || '未知项目类型'
}

// 时间处理工具
export const timeUtils = {
  /**
   * 格式化时间为 yyyy-MM-dd HH:mm:ss 格式
   * @param {Date|string|number} time - 时间对象、时间字符串或时间戳
   * @param {string} format - 输出格式，默认为 'yyyy-MM-dd HH:mm:ss'
   * @returns {string} 格式化后的时间字符串
   */
  formatTime(time, format = 'yyyy-MM-dd HH:mm:ss') {
    if (!time) return ''

    const date = new Date(time)
    if (isNaN(date.getTime())) return ''

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')

    return format
      .replace('yyyy', year)
      .replace('MM', month)
      .replace('dd', day)
      .replace('HH', hours)
      .replace('mm', minutes)
      .replace('ss', seconds)
  },

  /**
   * 获取当前时间的格式化字符串
   * @param {string} format - 输出格式，默认为 'yyyy-MM-dd HH:mm:ss'
   * @returns {string} 当前时间的格式化字符串
   */
  getCurrentTime(format = 'yyyy-MM-dd HH:mm:ss') {
    return this.formatTime(new Date(), format)
  },

  /**
   * 解析时间字符串为 Date 对象
   * @param {string} timeStr - 时间字符串
   * @returns {Date|null} Date 对象或 null
   */
  parseTime(timeStr) {
    if (!timeStr) return null
    const date = new Date(timeStr)
    return isNaN(date.getTime()) ? null : date
  },

  /**
   * 验证时间格式是否为 yyyy-MM-dd HH:mm:ss
   * @param {string} timeStr - 时间字符串
   * @returns {boolean} 是否为有效格式
   */
  isValidTimeFormat(timeStr) {
    if (!timeStr) return false
    const regex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/
    return regex.test(timeStr) && !isNaN(new Date(timeStr).getTime())
  },

  /**
   * 比较两个时间的大小
   * @param {string|Date} time1 - 时间1
   * @param {string|Date} time2 - 时间2
   * @returns {number} -1: time1 < time2, 0: time1 = time2, 1: time1 > time2
   */
  compareTime(time1, time2) {
    const date1 = new Date(time1)
    const date2 = new Date(time2)

    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) return 0

    if (date1 < date2) return -1
    if (date1 > date2) return 1
    return 0
  }
}

// 文件上传相关常量
export const UPLOAD_CONSTANTS = {
  // 分块上传大小 10MB
  CHUNK_SIZE: 10 * 1024 * 1024,
  // 支持的文件类型
  SUPPORTED_FILE_TYPES: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx,.zip,.rar',
  // 上传状态
  STATUS: {
    UPLOADING: 'uploading',
    SUCCESS: 'success',
    ERROR: 'error'
  }
}

// 业务状态常量
export const BUSINESS_CONSTANTS = {
  // 购标状态
  ORDER_STATUS: {
    NOT_PURCHASED: 0,
    PURCHASED: 1,
    PAID: 2
  },
  // 报名状态
  REGISTRATION_STATUS: {
    DRAFT: '0',
    SUBMITTED: '1'
  },
  // 分页大小选项
  PAGE_SIZES: [10, 20, 50, 100],
  DEFAULT_PAGE_SIZE: 10
}

// 招标文件类型枚举
export const TENDER_FILE_TYPE = {
  // 招标主文件
  MAIN_FILE: 'invitation-main',
  // 招标文件附件
  ATTACHMENT: 'invitation-att',
  // 投标主文件
  BID_FILE: 'bid-main',
  // 投标附件
  BID_FILE_ATTACHMENT: 'bid-att',
  // 澄清主文件
  CLARIFICATION_MAIN_FILE: 'invitation-main-clarification',
  // 澄清附件
  CLARIFICATION_ATTACHMENT: 'invitation-att-clarification'
}

// 表单验证规则
export const VALIDATION_RULES = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  mobile: /^1[3-9]\d{9}$/,
  phone: /^(\d{3,4}-?)?\d{7,8}$/
}


// 导出环境标识
export const env = {
  isDevelopment,
  isProduction,
  isTest: !isDevelopment && !isProduction
}
